import { Box, Button, Theme, Typography } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { useSession } from "@outschool/ui-auth";
import React from "react";

import { ActivityListing_ListingFragment } from "../../../../../shared/generated/graphql";
import { UpcomingClass } from "./UpcomingClass";

type UpcomingClassesWidgetProps = {
  upcomingClasses: ActivityListing_ListingFragment[];
  hasRequestableClasses: boolean;
  canViewCalendar: boolean;
  handleViewCalendarClick: () => void;
  handleSeeMoreClick: () => void;
  canParentEnroll: boolean;
};

export const UpcomingClassesWidget: React.FunctionComponent<
  UpcomingClassesWidgetProps
> = ({
  upcomingClasses,
  hasRequestableClasses,
  canParentEnroll,
  canViewCalendar,
  handleViewCalendarClick,
  handleSeeMoreClick
}) => {
  const { isLoggedIn } = useSession();
  const { t } = useTranslation(
    "client\\components\\teacher\\TeacherProfile\\UpcomingClassesWidget"
  );
  const hasUpcomingClasses = upcomingClasses.length > 0;

  return (
    <Box
      flex
      sx={{
        flexDirection: "column",
        justifyContent: "center",
        marginLeft: "94px"
      }}
    >
      <Box
        sx={{
          width: "372px",
          borderRadius: "8px",
          padding: "24px",
          boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.12)",
          border: "1px solid #DDDFE3"
        }}
      >
        <Typography
          variant="h3"
          sx={(theme: Theme) => ({
            fontSize: `${theme.typography.pxToRem(20)} !important`,
            paddingBottom: "4px"
          })}
        >
          {t`Upcoming Classes`}
        </Typography>

        {hasUpcomingClasses && canParentEnroll && (
          <>
            {upcomingClasses.map(upcomingClass => (
              <UpcomingClass
                key={
                  upcomingClass.nextSection?.uid ?? upcomingClass.activity.uid
                }
                upcomingClass={upcomingClass}
                openInSSRForLoggedOutUsers={!isLoggedIn}
              />
            ))}

            <Box
              flex
              sx={{
                paddingTop: "20px",
                justifyContent: "center"
              }}
            >
              <Button
                sx={{
                  width: "342px"
                }}
                onClick={
                  canViewCalendar ? handleViewCalendarClick : handleSeeMoreClick
                }
              >
                {canViewCalendar ? t`View Calendar` : t`See More`}
              </Button>
            </Box>
          </>
        )}

        {!hasUpcomingClasses && hasRequestableClasses && canParentEnroll && (
          <>
            <Typography
              variant="inherit"
              sx={{
                display: "block",
                paddingTop: "12px"
              }}
            >
              {t`Classes available by request`}
            </Typography>
            <Box
              flex
              sx={{
                paddingTop: "20px",
                justifyContent: "center"
              }}
            >
              <Button
                sx={{
                  width: "342px"
                }}
                onClick={handleSeeMoreClick}
              >
                {t`View Classes`}
              </Button>
            </Box>
          </>
        )}

        {((!hasUpcomingClasses && !hasRequestableClasses) ||
          !canParentEnroll) && (
          <Typography
            variant="inherit"
            sx={{
              display: "block",
              paddingTop: "12px"
            }}
          >
            {t`No scheduled classes available for enrollment`}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
